import { useCallback, useEffect, useRef, useState } from "react";
import { APP_BUILD_TIME, APP_VERSION } from "src/generated/buildMeta";
import { fetchRemoteVersion, isRemoteVersionNewer, RemoteVersionInfo } from "src/config/version";

const CHECK_INTERVAL_MS = 3 * 60 * 1000;
const SNOOZE_KEY = "isf_version_update_snooze_until";

const isSnoozed = () => {
  const until = Number(sessionStorage.getItem(SNOOZE_KEY) || 0);
  return until > Date.now();
};

export const useVersionUpdate = (enabled = true) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState<RemoteVersionInfo | null>(null);
  const checkingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (!enabled || checkingRef.current || isSnoozed()) return;
    checkingRef.current = true;

    try {
      const remote = await fetchRemoteVersion();
      if (!remote) return;

      if (isRemoteVersionNewer(remote, APP_VERSION, APP_BUILD_TIME)) {
        setRemoteVersion(remote);
        setUpdateAvailable(true);
      }
    } finally {
      checkingRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    checkForUpdate();

    const intervalId = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    const onFocus = () => checkForUpdate();
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [checkForUpdate, enabled]);

  const snooze = useCallback((minutes = 5) => {
    sessionStorage.setItem(SNOOZE_KEY, String(Date.now() + minutes * 60 * 1000));
    setUpdateAvailable(false);
  }, []);

  const reloadNow = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    updateAvailable,
    remoteVersion,
    localVersion: APP_VERSION,
    localBuildTime: APP_BUILD_TIME,
    snooze,
    reloadNow,
  };
};
